import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/** Height of the sticky header row — the only part of #header-group that stays on screen. */
function getHeaderHeight() {
  const header = document.querySelector('#header-component');
  if (header instanceof HTMLElement && header.offsetHeight > 0) {
    return header.offsetHeight;
  }

  const headerHeight = getComputedStyle(document.body).getPropertyValue('--header-height');
  return parseFloat(headerHeight) || 0;
}

function findHashTarget(hashId) {
  if (!hashId) return null;

  let id = hashId;
  try {
    id = decodeURIComponent(hashId);
  } catch {
    id = hashId;
  }

  const byDataSectionId = document.querySelector(`[data-section-id="${CSS.escape(id)}"]`);
  if (byDataSectionId) return byDataSectionId;

  const byId = document.getElementById(id);
  if (byId) return byId;

  if (id.startsWith('shopify-section-')) {
    return document.getElementById(id);
  }

  return null;
}

function scrollElementBelowHeader(target) {
  const headerHeight = getHeaderHeight();
  if (headerHeight > 0) {
    target.style.scrollMarginTop = `${headerHeight}px`;
  }
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function scrollToHash() {
  const hash = window.location.hash;
  if (!hash || hash.length < 2) return;

  const target = findHashTarget(hash.slice(1));
  if (!target) return;

  scrollElementBelowHeader(target);
}

function scheduleScrollToHash() {
  requestAnimationFrame(() => {
    requestAnimationFrame(scrollToHash);
  });
}

function handleHashLinkClick(event) {
  const link = event.target.closest('a[href^="#"]');
  if (!link || link.getAttribute('href') === '#') return;

  const hash = link.getAttribute('href');
  if (!hash || hash.length < 2) return;

  const target = findHashTarget(hash.slice(1));
  if (!target) return;

  event.preventDefault();
  history.pushState(null, '', hash);
  scrollElementBelowHeader(target);
}

/** Bare `href="#"` links (e.g. mega-menu parent links) shouldn't jump to top. */
function handleEmptyHashLinkClick(event) {
  const link = event.target.closest('a[href="#"]');
  if (!link) return;
  event.preventDefault();
}

document.addEventListener('click', handleHashLinkClick, true);
document.addEventListener('click', handleEmptyHashLinkClick);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleScrollToHash);
} else {
  scheduleScrollToHash();
}

window.addEventListener('hashchange', scheduleScrollToHash);

// js/common/behavior/scroll-progress.js
var ScrollProgress = class extends HTMLElement {
  connectedCallback() {
    // Don't proceed if we can't find a scrolled element
    if (!this.scrolledElement) {
      console.warn('scroll-progress: No scrolled element found');
      return;
    }
    
    this.scrolledElement.addEventListener("scroll", throttle(this._updateScrollProgress.bind(this)));
    if (window.ResizeObserver) {
      new ResizeObserver(this._updateScrollProgress.bind(this)).observe(this.scrolledElement);
    }
  }
  
  get scrolledElement() {
    if (!this._scrolledElement) {
      // Check if we should observe a slideshow component
      const observesAttr = this.getAttribute("observes");
      if (observesAttr === 'slideshow-component') {
        // Find the nearest slideshow component and get its slides container
        const slideshow = this.closest('slideshow-component');
        this._scrolledElement = slideshow ? slideshow.querySelector('slideshow-slides') : null;
      } else if (observesAttr) {
        // Otherwise, use the selector from the attribute
        this._scrolledElement = this.closest(observesAttr);
      }
      // No fallback - only use what's explicitly specified
    }
    return this._scrolledElement;
  }
  
  _updateScrollProgress() {
    if (!this.scrolledElement) return;
    
    const scrollLeft = document.dir === "ltr" ? 
      this.scrolledElement.scrollLeft : 
      Math.abs(this.scrolledElement.scrollLeft);
    
    const advancement = (scrollLeft + this.scrolledElement.clientWidth) / this.scrolledElement.scrollWidth;
    this.style.setProperty("--scroll-progress", Math.max(0, Math.min(advancement, 1)).toFixed(6));
  }
};

if (!window.customElements.get("scroll-progress")) {
  window.customElements.define("scroll-progress", ScrollProgress);
}

function throttle(fn, delay) {
    let lastCall = 0;
    return function (...args) {
      const now = new Date().getTime();
      if (now - lastCall < delay) {
        return;
      }
      lastCall = now;
      return fn(...args);
    };
}


class VariantSelect extends Component {
  /** @type {AbortController | undefined} */
  #optionEventsAbort;

  connectedCallback() {
    this.select = this.querySelector("select");
    if (!this.select) return;

    this.wrapper = this.querySelector(".custom-select");
    this.display = this.querySelector(".cs-display");
    this.menu = this.querySelector(".cs-menu");
    this.optionEls = Array.from(this.querySelectorAll(".cs-option"));

    // Track if user has interacted yet
    this.userHasInteracted = false;
    // Direct variant links (?variant=) already resolve the full configuration — show real values, not placeholders
    if (new URLSearchParams(window.location.search).has('variant')) {
      this.userHasInteracted = true;
    }

    this.bindEvents();
    this.initializeSelectedOption();

    // Defer revealing the parent variant-picker until all sibling selects have initialized
    queueMicrotask(() => {
      const variantPicker = this.closest('variant-picker');
      if (variantPicker && variantPicker.hasAttribute('hidden')) {
        variantPicker.removeAttribute('hidden');
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#optionEventsAbort?.abort();
    this.#optionEventsAbort = undefined;
  }

  bindEvents() {
    this.wrapper.addEventListener("click", (e) => {
      e.stopPropagation();
      this.toggleMenu();
    });

    document.addEventListener("click", () => this.closeMenu());

    // Keyboard navigation
    this.wrapper.addEventListener("keydown", (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggleMenu();
      }
    });

    this.#bindOptionEvents();
  }

  #bindOptionEvents() {
    this.#optionEventsAbort?.abort();
    this.#optionEventsAbort = new AbortController();
    const { signal } = this.#optionEventsAbort;

    this.optionEls.forEach((optionEl, index) => {
      const option = this.select.options[index];
      if (!option) return;

      optionEl.addEventListener(
        'click',
        (e) => {
          e.stopPropagation();
          this.userHasInteracted = true;
          this.onOptionSelect(option, optionEl);
        },
        { signal }
      );

      optionEl.addEventListener(
        'keydown',
        (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.userHasInteracted = true;
            this.onOptionSelect(option, optionEl);
          }
        },
        { signal }
      );
    });
  }

  refresh() {
    this.select = this.querySelector("select");
    if (!this.select) return;

    this.wrapper = this.querySelector(".custom-select");
    this.display = this.querySelector(".cs-display");
    this.menu = this.querySelector(".cs-menu");
    this.optionEls = Array.from(this.querySelectorAll(".cs-option"));

    this.#bindOptionEvents();
    this.initializeSelectedOption();
  }

  /**
   * Called by variant-picker after section morph so reused hosts refresh refs,
   * re-bind option nodes, and restore placeholder vs. real selection state.
   * @param {Set<string>} interactedSelectNames - Native select `name` values the user has changed.
   */
  afterSectionMorph(interactedSelectNames) {
    this.select = this.querySelector('select');
    if (!this.select) return;

    this.wrapper = this.querySelector('.custom-select');
    this.display = this.querySelector('.cs-display');
    this.menu = this.querySelector('.cs-menu');
    this.optionEls = Array.from(this.querySelectorAll('.cs-option'));

    this.userHasInteracted = interactedSelectNames.has(this.select.name);

    this.#bindOptionEvents();
    this.initializeSelectedOption();
  }

  initializeSelectedOption() {
    const selectedOption = Array.from(this.select.options).find(opt => opt.selected);

    this.optionEls.forEach((el) => el.classList.remove('selected'));

    if (selectedOption) {
      // Check if we should show custom label on initial load
      const hasCustomLabel = this.display.hasAttribute('data-custom-label');

      if (hasCustomLabel && !this.userHasInteracted) {
        // On initial load: show custom label if it exists
        this.display.textContent = this.display.getAttribute('data-custom-label');
        this.classList.remove('has-value');
      } else {
        // Otherwise show the selected option text
        this.display.textContent = this.getDisplayText(selectedOption);
        this.classList.add("has-value");
      }

      // Still mark the option as selected in the menu
      const optionIndex = Array.from(this.select.options).indexOf(selectedOption);
      if (this.optionEls[optionIndex]) {
        this.optionEls[optionIndex].classList.add("selected");
      }
    } else {
      // Select first available option by default
      const firstAvailable = Array.from(this.select.options).find(opt => !opt.disabled);
      if (firstAvailable) {
        this.select.value = firstAvailable.value;
        this.onOptionSelect(firstAvailable, this.optionEls[0]);
      }
    }
  }

  getDisplayText(option) {
    if (option.text.includes('content.unavailable')) {
      return option.text.split(' - ')[0];
    }
    return option.text;
  }

  toggleMenu() {
    const isOpening = !this.menu.classList.contains("open");
    
    if (isOpening) {
      // Close all other variant-select menus on the page
      document.querySelectorAll('variant-select .cs-menu.open').forEach(otherMenu => {
        if (otherMenu !== this.menu) {
          otherMenu.classList.remove('open');
          otherMenu.closest('variant-select').wrapper.setAttribute('aria-expanded', 'false');
        }
      });
    }
    
    this.menu.classList.toggle("open");
    this.wrapper.setAttribute('aria-expanded', this.menu.classList.contains("open"));
  }

  closeMenu() {
    this.menu.classList.remove("open");
    this.wrapper.setAttribute('aria-expanded', 'false');
  }

  onOptionSelect(option, optionEl) {
    if (option.disabled) return;
    
    // Update native select
    this.select.value = option.value;
    
    // Trigger variant change
    this.select.dispatchEvent(new Event("change", { 
      bubbles: true, 
      composed: true 
    }));
    
    // Update display - always show selected option text after user interaction
    this.display.textContent = this.getDisplayText(option);
    
    // Update selected styling
    this.optionEls.forEach(el => el.classList.remove("selected"));
    optionEl.classList.add("selected");
    
    // Update has-value class
    this.classList.add("has-value");
    
    this.closeMenu();

    // Re-evaluate add-to-cart disabled state
    checkAndApplyDisable();
  }
}

customElements.define("variant-select", VariantSelect);


// Listen for variant update
document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  sendPdpConfigEvent(event);
  paypalMsg(event);
  checkAndApplyDisable();
  setTimeout(() => updateVariantMessageVisibility(), 0);
});


function sendPdpConfigEvent(event) {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'pdp_config', {
      event_category: 'Models',
      event_label: 'Product page',
      pdp_model_name:
        event.detail.resource.name,
      page_context: 'Product page'
    });
  } else {
    console.log('gtag not found, event not sent');
  }
}

function paypalMsg(event) {
  if (!event.detail.resource) return;

  document.querySelectorAll('[data-pp-amount]').forEach(e => {
    e.setAttribute('data-pp-amount', (event.detail.resource.price / 100).toFixed(2));

    // if (paypal) {
    //   paypal.Messages({
    //     amount: event.detail.resource.price / 100,
    //     pageType: "product-details",
    //     style: {
    //       layout: "text",
    //       logo: {
    //       type: "inline",
    //       },
    //     },
    //   })
    //   .render('.pp-message');
    // }
  });
}

/**
 * A custom element that displays variant messages.
 * @extends Component
 */
export class VariantMessage extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { capture: true, signal });

    // Inject the status field on initial load
    this.#injectStatusField();

    queueMicrotask(() => updateVariantMessageVisibility());
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;

    const newVariantMessage = source.querySelector('variant-message');
    if (!newVariantMessage) return;

    const hasContent = newVariantMessage.hasAttribute('data-variant-message-has-content');
    const reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (hasContent && !reduceMotion) {
      newVariantMessage.style.opacity = '0';
    }

    this.replaceWith(newVariantMessage);
    // Same turn as replace: avoid one paint with SSR `hidden` + multi-option (Liquid) before gating clears it.
    updateVariantMessageVisibility();

    if (hasContent && !reduceMotion) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          newVariantMessage.style.transition = 'opacity 0.22s ease';
          newVariantMessage.style.opacity = '1';
          const cleanup = () => {
            newVariantMessage.style.transition = '';
            newVariantMessage.style.opacity = '';
          };
          newVariantMessage.addEventListener('transitionend', cleanup, { once: true });
          setTimeout(cleanup, 400);
        });
      });
    }
  };

  /**
   * Ensures that every product form contains the current status value.
   */
  #injectStatusField() {
    const forms = document.querySelectorAll('form[id^="BuyButtons-ProductForm-"]');
    if (!forms.length) return;

    const statusInputs = document.querySelectorAll('input.cart-message');
    if (!statusInputs.length) {
      forms.forEach((form) => {
        form.querySelectorAll('input.cart-message').forEach((inp) => inp.remove());
      });
      return;
    }

    const currentStatus = statusInputs[0].value;

    forms.forEach((form) => {
      let existingField = form.querySelector('input.cart-message');
      if (existingField) {
        existingField.value = currentStatus;
      } else {
        const clonedInput = statusInputs[0].cloneNode(true);
        clonedInput.removeAttribute('form');
        clonedInput.type = 'hidden';
        form.appendChild(clonedInput);
      }
    });
  }
}

if (!customElements.get('variant-message')) {
  customElements.define('variant-message', VariantMessage);
}

/**
 * Checks for mobile variant parameter and triggers ONLY media gallery update
 */
function checkAndApplyMobileVariant() {
  // Only run once
  if (checkAndApplyMobileVariant.hasRun) return;
  checkAndApplyMobileVariant.hasRun = true;

  // Detect mobile using matchMedia
  const isMobile = window.matchMedia('(max-width: 749px)').matches;

  // Detect if initial navigation URL had a variant parameter
  const navEntry = performance.getEntriesByType('navigation')[0];
  const hasInitialVariant = navEntry && navEntry.name.includes('variant=');

  // Check conditions
  if (isMobile &&
      document.querySelector('.variant-option__select') &&
      hasInitialVariant) {

    // Get the variant parameter from the initial URL
    const urlParams = new URLSearchParams(new URL(navEntry.name).search);
    const variantId = urlParams.get('variant');

    if (variantId) {
      // Find the variant-picker to get the updated HTML
      const variantPicker = document.querySelector('variant-picker');

      if (variantPicker) {
        // Find the selected option for this variant
        const selectedOption = variantPicker.querySelector(`[data-variant-id="${variantId}"]`);

        if (selectedOption) {
          // Fetch ONLY for media gallery update
          fetchMediaGalleryForVariant(variantPicker, selectedOption);
        }
      }
    }
  }
}


/**
 * Fetches updated HTML and triggers media gallery update only
 */
async function fetchMediaGalleryForVariant(variantPicker, selectedOption) {
  // Build the request URL using variant-picker's method
  const requestUrl = variantPicker.buildRequestUrl(selectedOption);
  
  try {
    const response = await fetch(requestUrl);
    const responseText = await response.text();
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    
    // Find all media-gallery elements
    const mediaGalleries = document.querySelectorAll('media-gallery');
    
    mediaGalleries.forEach(mediaGallery => {
      // Get the new media-gallery from the fetched HTML
      const newMediaGallery = html.querySelector('media-gallery');
      
      if (newMediaGallery) {
        // Replace the current media gallery with the new one
        mediaGallery.replaceWith(newMediaGallery.cloneNode(true));
      }
    });
    
  } catch (error) {
    console.error('Error fetching media gallery:', error);
  }
}

// Run the function when DOM is loaded
checkAndApplyMobileVariant();

function onAcceleratedCheckoutRendered(root, callback) {
  if (!root) return;

  const hasRenderedSomething = () => {
    // Minimal check: payment button root exists and has content
    const paymentButton = root.querySelector('.shopify-payment-button');
    return paymentButton && paymentButton.childElementCount > 0;
  };

  // If it’s already rendered, fire immediately
  if (hasRenderedSomething()) callback();

  const obs = new MutationObserver(() => {
    if (hasRenderedSomething()) callback();
  });

  obs.observe(root, { childList: true, subtree: true });

  return () => obs.disconnect(); // optional cleanup
}

const container = document.querySelector('.accelerated-checkout-block');

function getMainProductForm() {
  const picker = getMainVariantPicker();
  const section = picker?.closest('.shopify-section');
  const forms = /** @type {NodeListOf<HTMLElement>} */ (section
    ? section.querySelectorAll('product-form-component')
    : document.querySelectorAll('product-form-component'));

  if (picker instanceof HTMLElement && picker.dataset.productId) {
    for (const form of forms) {
      if (form.dataset?.productId === picker.dataset.productId) return form;
    }
  }

  return forms[0] || null;
}

/** @param {HTMLElement | null} [productForm] */
function variantSelectionBlocksAddToCart(productForm = getMainProductForm()) {
  const variantCount = parseInt(productForm?.dataset?.variantCount || '1', 10);
  return variantCount > 1 && productForm?.dataset?.variantSelectionResolved !== 'true';
}

/** @param {HTMLElement | null} [productForm] */
function applyDisabled(productForm = getMainProductForm()) {
  if (!productForm) return;

  const addToCartEls = productForm.querySelectorAll('[ref="addToCartButton"].add-to-cart-button');
  addToCartEls.forEach((e) => {
    e.setAttribute('disabled', '');
  });

  const checkoutEl = productForm.querySelector('.shopify-payment-button shopify-accelerated-checkout');
  if (checkoutEl) {
    checkoutEl.setAttribute('disabled', '');
  }
}

/** @param {HTMLElement | null} [productForm] */
function applyEnabled(productForm = getMainProductForm()) {
  if (!productForm || shouldDisable(productForm)) return;

  const addToCartEls = productForm.querySelectorAll('[ref="addToCartButton"].add-to-cart-button');
  addToCartEls.forEach((e) => {
    e.removeAttribute('disabled');
  });

  const checkoutEl = productForm.querySelector('.shopify-payment-button shopify-accelerated-checkout');
  if (checkoutEl) {
    checkoutEl.removeAttribute('disabled');
  }
}

/** Main PDP variant picker (excludes quick-add / cards). */
function getMainVariantPicker() {
  return (
    document.querySelector('variant-picker[data-template-product-match="true"]') ||
    document.querySelector('variant-picker[data-variant-option-count]') ||
    document.querySelector('variant-picker')
  );
}

/** variant-select nodes in the main PDP picker (not quick-add / cards). */
function getMainPickerVariantSelects() {
  const picker = getMainVariantPicker();
  return picker ? picker.querySelectorAll('variant-select') : document.querySelectorAll('variant-select');
}

/** True when every custom dropdown (variant-select) has a real user selection (not placeholder). */
function allVariantSelectsInteracted() {
  const selects = getMainPickerVariantSelects();
  if (selects.length === 0) return true;

  for (const vs of selects) {
    if (!vs.userHasInteracted) {
      return false;
    }
  }
  return true;
}

/**
 * Only 2+ custom dropdowns need explicit interaction before add-to-cart (combined options).
 * 0 dropdowns (buttons/swatches) or 1 dropdown: native select already reflects the resolved variant.
 */
function variantSelectsBlockAddToCart() {
  const selects = getMainPickerVariantSelects();
  if (selects.length <= 1) return false;
  for (const vs of selects) {
    if (!vs.userHasInteracted) return true;
  }
  return false;
}

/** Main PDP recessed picker (excludes quick-add / cards). */
function getMainRecessedPicker() {
  return (
    document.querySelector('recessed-picker[data-template-product-match="true"]') ||
    document.querySelector('recessed-picker')
  );
}

/**
 * Recessed hub PDPs gate the variant-message on explicit Vehicle Model selection.
 * Returns true only when the Model `recessed-select` has been interacted with and
 * the display is no longer showing its placeholder custom label.
 */
function isRecessedModelSelected() {
  const picker = getMainRecessedPicker();
  if (!picker) return false;

  const modelRecessedSelect = picker.querySelector('recessed-select:has(select[name*="Model"])');
  if (!modelRecessedSelect) return false;

  if (modelRecessedSelect.userHasInteracted !== true) return false;

  const select = modelRecessedSelect.querySelector('select[name*="Model"]');
  const value = select?.value?.trim();
  if (!value || value === 'unselected') return false;

  const display = modelRecessedSelect.querySelector('.cs-display');
  const customLabel = display?.dataset.customLabel;
  if (display && customLabel && display.textContent.trim() === customLabel.trim()) return false;

  return true;
}

/** Hide variant-message until every main-picker custom dropdown has a real selection (not placeholder). */
function updateVariantMessageVisibility() {
  const recessedPicker = getMainRecessedPicker();
  document.querySelectorAll('variant-message[data-variant-message-has-content]').forEach((el) => {
    if (recessedPicker) {
      el.hidden = !isRecessedModelSelected();
      return;
    }
    const selects = getMainPickerVariantSelects();
    const waitsForDropdownSelection = selects.length > 0 && !allVariantSelectsInteracted();
    el.hidden = waitsForDropdownSelection;
  });
}

window.updateVariantMessageVisibility = updateVariantMessageVisibility;

// variant-split.js re-evaluates the add-to-cart gate when a brand re-pick
// un-resolves the hidden option until a model is chosen again.
window.checkAndApplyDisable = checkAndApplyDisable;

/** @param {HTMLElement | null} [productForm] */
function shouldDisable(productForm = getMainProductForm()) {
  // Get URL search params
  const urlParams = new URLSearchParams(window.location.search);

  // Check if URL contains both 'make=' and 'model=' parameters
  if (urlParams.has('make') && urlParams.has('model') && productForm) {
    productForm.dataset.variantSelectionResolved = 'true';
  }

  if (productForm?.dataset?.canAddToCart === 'false') {
    return true;
  }

  if (urlParams.has('variant')) {
    if (productForm) productForm.dataset.variantSelectionResolved = 'true';
  }

  if (variantSelectionBlocksAddToCart(productForm)) {
    return true;
  }

  const hasVariantSelects = getMainPickerVariantSelects().length > 0 || document.querySelector('recessed-select');
  if (hasVariantSelects) {
    return variantSelectsBlockAddToCart();
  }

  return false;
}

function checkAndApplyDisable() {
  const productForm = getMainProductForm();
  if (productForm) {
    if (shouldDisable(productForm)) {
      applyDisabled(productForm);
    } else {
      applyEnabled(productForm);
    }
  }
  updateVariantMessageVisibility();
}

// Set up URL change detection
let lastUrl = location.href;
function setupUrlChangeObserver() {
  new MutationObserver(() => {
    const url = location.href;
    if (url !== lastUrl) {
      lastUrl = url;
      // URL changed, check if we need to apply/remove disabled state
      checkAndApplyDisable();
    }
  }).observe(document, { subtree: true, childList: true });
}

onAcceleratedCheckoutRendered(document.querySelector('.accelerated-checkout-block'), () => {
  // Initial check
  checkAndApplyDisable();
  
  // Keep watching for DOM changes (payment button re-renders)
  const obs = new MutationObserver(() => {
    checkAndApplyDisable();
  });
  obs.observe(container, { childList: true, subtree: true });
  
  // Set up URL change observer
  setupUrlChangeObserver();
});

// Also check on initial page load
document.addEventListener('DOMContentLoaded', () => {
  setTimeout(() => {
    checkAndApplyDisable();
  }, 100); // Small delay to ensure DOM is fully loaded
});

// Alternative: Use history API to detect URL changes
(function(history) {
  const pushState = history.pushState;
  const replaceState = history.replaceState;
  
  history.pushState = function(state, title, url) {
    const result = pushState.apply(history, arguments);
    window.dispatchEvent(new Event('pushstate'));
    window.dispatchEvent(new Event('locationchange'));
    return result;
  };
  
  history.replaceState = function(state, title, url) {
    const result = replaceState.apply(history, arguments);
    window.dispatchEvent(new Event('replacestate'));
    window.dispatchEvent(new Event('locationchange'));
    return result;
  };
  
  window.addEventListener('popstate', function() {
    window.dispatchEvent(new Event('locationchange'));
  });
})(window.history);

// Listen for location changes
window.addEventListener('locationchange', function() {
  checkAndApplyDisable();
});
/* ------------------------------------------------------------------
 * Recessed cut-out gate (blocks/recessed-cutout-choice.liquid)
 * Blocks add to cart / buy now until the cut-out notice is ticked ("I understand").
 * Listens on window in the capture phase: Horizon's on:click / on:submit handlers are delegated
 * from document (capture), so a document listener registered later would run after them.
 * ---------------------------------------------------------------- */
(function () {
  const GATE = '.recessed-cutout-choice';
  const ROW = '[data-cutout-row]';
  const BUTTONS = '.add-to-cart-button, .sticky-add-to-cart__button, button[name="add"]';

  /** Unticked acknowledgements across all active gates inside a scope element. */
  function unansweredRows(scope, formId) {
    const rows = [];
    scope.querySelectorAll(GATE).forEach((gate) => {
      if (gate.disabled || (formId && gate.dataset.formId !== formId)) return;
      gate.querySelectorAll(ROW).forEach((row) => {
        if (!row.querySelector('input:checked')) rows.push(row);
      });
    });
    return rows;
  }

  function flag(rows) {
    rows.forEach((row) => row.classList.add('is-unanswered-highlight'));
    const first = rows[0];
    first.scrollIntoView({ behavior: 'smooth', block: 'center' });
    first.querySelector('input')?.focus({ preventScroll: true });
  }

  function block(event, rows) {
    event.preventDefault();
    event.stopImmediatePropagation();
    flag(rows);
  }

  window.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      // getAttribute: the product form has <input name="id">, which shadows form.id
      const formId = form instanceof HTMLFormElement ? form.getAttribute('id') : null;
      if (!formId) return;
      const rows = unansweredRows(document, formId);
      if (rows.length) block(event, rows);
    },
    true
  );

  // Covers the sticky bar (incl. its "buy now" path which bypasses form submit) and any button click.
  window.addEventListener(
    'click',
    (event) => {
      const button = event.target instanceof Element ? event.target.closest(BUTTONS) : null;
      if (!button) return;
      const scope = button.closest('.shopify-section, dialog') || document;
      const rows = unansweredRows(scope);
      if (rows.length) return block(event, rows);

      // Sticky "buy now" jumps to /cart/{id}:{qty} and skips the form, which would drop Special Requests
      // (where the notice tells customers to ask for cut-outs). On gated products add through the
      // form, then go to checkout.
      if (button.matches('.sticky-add-to-cart__button') && button.getAttribute('on:click') === '/handleBuyClick') {
        const gate = scope.querySelector(GATE);
        const form = gate && document.getElementById(gate.dataset.formId);
        if (!(form instanceof HTMLFormElement)) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        const root = window.Shopify?.routes?.root || '/';
        button.disabled = true;
        fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        })
          .then((response) => {
            if (!response.ok) throw new Error(`cart/add ${response.status}`);
            window.location.href = `${root}checkout`;
          })
          .catch(() => {
            // Let the main form show the theme's own error (sold out, quantity limits...)
            button.disabled = false;
            form.querySelector('.add-to-cart-button')?.click();
          });
      }
    },
    true
  );

  // Variant-filtered gates (e.g. FuelTech only): show + enable only while a matching variant is selected.
  document.addEventListener(
    ThemeEvents.variantUpdate,
    (event) => {
      const scope = (event.target instanceof Element && event.target.closest('.shopify-section, dialog')) || document;
      const variantId = event.detail?.resource?.id;
      scope.querySelectorAll(`${GATE}[data-variant-ids]`).forEach((gate) => {
        const active = variantId != null && gate.dataset.variantIds.split(',').includes(String(variantId));
        gate.hidden = !active;
        gate.disabled = !active;
      });
    },
    true
  );

  document.addEventListener('change', (event) => {
    const row = event.target instanceof Element ? event.target.closest(ROW) : null;
    row?.classList.remove('is-unanswered-highlight');
  });
})();
